import React from 'react';
import { FileText, CheckCircle, AlertTriangle, Phone, Users, Link2, Zap, Bell, ClipboardCheck } from 'lucide-react';

export const OpsSopPage = () => {
  const steps = [
    {
      number: 1,
      title: "Verify Signup Details",
      icon: Users,
      description: "Check client information is correct: business name, industry, email, phone, plan, setup fee status.",
      portal: "Ops → Clients → [Client]",
      checklist: [
        "Business name correct",
        "Industry correct", 
        "Contact email correct",
        "Phone number correct",
        "Plan matches payment",
        "Setup fee shows 'Paid'"
      ]
    },
    {
      number: 2,
      title: "Collect Business Rules",
      icon: FileText,
      description: "Configure business hours, services, locations, and calendar integration.",
      portal: "Ops → Clients → [Client] → Settings",
      checklist: [
        "Business hours set for each day",
        "Services listed with durations",
        "Locations added (if multiple)",
        "Calendar email configured"
      ]
    },
    {
      number: 3,
      title: "Create SigmaMind Agent",
      icon: Zap,
      description: "Create the voice agent in SigmaMind with proper playbook and webhooks.",
      portal: "SigmaMind Dashboard (External)",
      checklist: [
        "Agent created with correct voice",
        "Industry playbook selected",
        "Greeting includes business name",
        "Webhook URL configured with secret token",
        "Can-accept URL configured",
        "Note agent_id and SIP number"
      ]
    },
    {
      number: 4,
      title: "Link Agent in Calyro",
      icon: Link2,
      description: "Connect the SigmaMind agent to the client in Calyro.",
      portal: "Ops → Link Agent",
      checklist: [
        "Agent ID entered correctly",
        "Client selected",
        "Location ID set",
        "SIP number entered",
        "Industry matches client"
      ]
    },
    {
      number: 5,
      title: "Run QA Checklist",
      icon: ClipboardCheck,
      description: "Make a test call and verify all 6 QA items pass.",
      portal: "Ops → Clients → [Client] → Run QA",
      checklist: [
        "Call connects successfully",
        "Voice quality acceptable",
        "Correct greeting & business name",
        "At least one intent handled correctly",
        "Lead captured OR booking flow triggered",
        "Recording + summary received"
      ],
      critical: true
    },
    {
      number: 6,
      title: "Activate Client",
      icon: Zap,
      description: "Make the client LIVE. Requires all gates to pass.",
      portal: "Ops → Clients → [Client] → Activate",
      checklist: [
        "Setup fee paid ✓",
        "Agent linked ✓",
        "QA passed ✓",
        "Click Activate button"
      ],
      critical: true
    },
    {
      number: 7,
      title: "Verification Call",
      icon: Phone,
      description: "Place a live test call to verify end-to-end flow.",
      portal: "Personal phone → SIP number",
      checklist: [
        "can-accept returns 'allowed: true'",
        "Webhook arrives at Calyro",
        "Call appears in dashboard",
        "Usage minutes updated"
      ]
    },
    {
      number: 8,
      title: "Notify Client",
      icon: Bell,
      description: "Send go-live notification to the client.",
      portal: "Email/Message",
      checklist: [
        "Confirm they are LIVE",
        "Share SIP number",
        "Provide dashboard access",
        "Share support contact"
      ]
    },
    {
      number: 9,
      title: "Complete Onboarding",
      icon: CheckCircle,
      description: "Mark onboarding complete and set up monitoring.",
      portal: "Ops → Clients → [Client]",
      checklist: [
        "Add completion note with date",
        "Assign monitoring owner",
        "Set 7-day check-in reminder"
      ]
    }
  ];

  const plans = [
    { name: "Receptionist", minutes: "300/mo", locations: "1", price: "$199/mo", setup: "$299" },
    { name: "Front Desk", minutes: "800/mo", locations: "3", price: "$399/mo", setup: "$499" },
    { name: "Regional HQ", minutes: "2000/mo", locations: "Unlimited", price: "$799/mo", setup: "$999" },
  ];

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <FileText className="w-7 h-7 text-emerald-600" />
          Client Onboarding SOP
        </h1>
        <p className="text-gray-600 mt-1">Standard Operating Procedure • Version 1.1</p>
      </div>

      {/* Important Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">All steps must be completed in order</p>
          <p className="text-sm text-amber-700">Skipping or reordering steps will result in system blocks. Critical gates are enforced by the system.</p>
        </div>
      </div>

      {/* Plan Reference */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <h2 className="font-semibold text-gray-900 mb-4">Plan Reference (Fixed Pricing)</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-sm font-medium text-gray-500">Plan</th>
                <th className="text-left py-2 text-sm font-medium text-gray-500">Minutes</th>
                <th className="text-left py-2 text-sm font-medium text-gray-500">Locations</th>
                <th className="text-left py-2 text-sm font-medium text-gray-500">Price</th>
                <th className="text-left py-2 text-sm font-medium text-gray-500">Setup Fee</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(plan => (
                <tr key={plan.name} className="border-b border-gray-100">
                  <td className="py-3 font-medium text-gray-900">{plan.name}</td>
                  <td className="py-3 text-gray-700">{plan.minutes}</td>
                  <td className="py-3 text-gray-700">{plan.locations}</td>
                  <td className="py-3 text-gray-700">{plan.price}</td>
                  <td className="py-3 text-gray-700">{plan.setup}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-gray-500 mt-3">
          Plans are set at signup via Stripe. Ops cannot change plans.
        </p>
      </div>

      {/* Steps */}
      <div className="space-y-6">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          return (
            <div 
              key={step.number} 
              className={`bg-white rounded-xl shadow-sm overflow-hidden ${step.critical ? 'ring-2 ring-amber-300' : ''}`}
            >
              <div className={`p-6 ${step.critical ? 'bg-amber-50' : ''}`}>
                <div className="flex items-start gap-4">
                  {/* Step Number */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${step.critical ? 'bg-amber-500' : 'bg-emerald-600'}`}>
                    {step.number}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{step.title}</h3>
                      {step.critical && (
                        <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded-full">
                          Critical Gate
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-3">{step.description}</p>
                    
                    {/* Portal */}
                    <div className="text-sm text-gray-500 mb-3">
                      <span className="font-medium">Portal:</span> {step.portal}
                    </div>
                    
                    {/* Checklist */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Checklist:</p>
                      <ul className="space-y-1">
                        {step.checklist.map((item, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                            <span className="w-4 h-4 border border-gray-300 rounded flex-shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Troubleshooting */}
      <div className="mt-8 bg-white rounded-xl shadow-sm p-6">
        <h2 className="font-semibold text-gray-900 mb-4">Troubleshooting</h2>
        
        <div className="space-y-4">
          <div>
            <p className="font-medium text-gray-800">Activation Blocked?</p>
            <ul className="text-sm text-gray-600 ml-4 mt-1 list-disc">
              <li>Setup fee not paid → Check Stripe dashboard</li>
              <li>No agent linked → Complete Step 4</li>
              <li>QA not passed → Complete Step 5</li>
            </ul>
          </div>
          
          <div>
            <p className="font-medium text-gray-800">Webhook Not Arriving?</p>
            <ul className="text-sm text-gray-600 ml-4 mt-1 list-disc">
              <li>Verify webhook URL includes secret token</li>
              <li>Check Admin → Webhook Health</li>
              <li>Check Admin → Quarantine for errors</li>
            </ul>
          </div>
          
          <div>
            <p className="font-medium text-gray-800">Call Not Showing in Dashboard?</p>
            <ul className="text-sm text-gray-600 ml-4 mt-1 list-disc">
              <li>Verify can-accept returned "allowed: true"</li>
              <li>Check agent_id matches exactly</li>
              <li>Review quarantine for errors</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>SOP Version 1.1 • Last updated December 2025</p>
        <p>Questions? Contact ops@calyro.ai</p>
      </div>
    </div>
  );
};
